import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useApolloClient, useQuery, useLazyQuery } from "react-apollo";
import { Input, Field, Control, Label, Icon, Button } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import gql from "graphql-tag";
import moment from "moment";
import { OrdersTable } from "../../../../components/Orders";
import {
  UPDATE_ORDER_MUTATION,
  ALL_ORDERS_VIEW_BY_PAGE_QUERY
} from "../../../../graphql/Orders/Orders.graphql";
import { ALL_COMPANY_POSTING_PERIODS_QUERY } from "../../../../graphql/CompanyPostingPeriods/CompanyPostingPeriods.graphql";
import Loading from "../../../../components/Loading";
import { ourToast as toast, runPromisesInSeries } from "../../../../utils";
import { BILLING_VERIFIED_COLUMNS } from "../columns";
import ApolloDateInput from "../../../../components/ApolloDateInput";

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const PERFORM_INVOICE_GENERATION_MUTATION = gql`
  mutation performInvoiceGeneration(
    $scheduledDeliveryDate: DateTime!
    $invoiceDate: DateTime!
  ) {
    performInvoiceGeneration(
      scheduledDeliveryDate: $scheduledDeliveryDate
      invoiceDate: $invoiceDate
    ) {
      RecId
      StatusRecIdRef
      InvoiceNumber
      InvoiceDate
    }
  }
`;

class CustomBillingVerifiedError extends Error {}

const BillingVerified = () => {
  const SystemCodes = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order"],
        CodeName_in: ["OrderStatus"],
        CodeId_in: ["RV", "BV"]
      }
    }
  });

  const { data: PostingModulesData } = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        CodeName: "PostingModule",
        Category: "PostingPeriods",
        CodeId: "F"
      }
    }
  });
  const { data: PostingPeriodsData } = useQuery(
    ALL_COMPANY_POSTING_PERIODS_QUERY,
    {
      variables: {
        where: {
          Open: true,
          ModuleRecIdRef: PostingModulesData?.SystemCodes[0]?.RecId
        }
      },
      fetchPolicy: "network-only"
    }
  );

  const OpenFinanceCompanyPostingPeriod =
    PostingPeriodsData?.CompanyPostingPeriods[0];

  const client = useApolloClient();
  const history = useHistory();
  const [where, setWhere] = useState({});
  const [loading, setLoading] = useState(false);

  const [States, setStates] = useState(null);
  const [selectedFlatRows, setSelectedFlatRows] = useState([]);
  const [scheduledDeliveryDate, setScheduledDeliveryDate] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");

  const [getOrdersData, { data: OrdersData }] = useLazyQuery(
    ALL_ORDERS_VIEW_BY_PAGE_QUERY
  );

  // const { data: OrdersData } = useQuery(ALL_ORDERS_VIEW_BY_PAGE_QUERY, {
  //   variables: where ? { where } : {}
  // });

  const handleGenerateInvoices = async () => {
    try {
      setLoading(true);
      const openPeriodDate = new Date(
        OpenFinanceCompanyPostingPeriod?.PeriodEndDateTime
      );

      if (new Date(scheduledDeliveryDate) > openPeriodDate) {
        throw new CustomBillingVerifiedError(
          "Delivery Date is greater than current open Posting period."
        );
      }
      if (new Date(invoiceDate) > openPeriodDate) {
        throw new CustomBillingVerifiedError(
          "Invoice is greater than current open Posting period."
        );
      }

      await client.mutate({
        mutation: PERFORM_INVOICE_GENERATION_MUTATION,
        variables: {
          scheduledDeliveryDate: moment(scheduledDeliveryDate)
            .endOf("day")
            .toDate(),
          invoiceDate: new Date(invoiceDate).setUTCHours(13)
        },
        refetchQueries: ["ALL_ORDERS_VIEW_BY_PAGE_QUERY"]
      });

      toast.success("Invoices imported successfully");
    } catch (err) {
      const message =
        err instanceof CustomBillingVerifiedError
          ? err.message
          : "Whoops! Something went wrong. Please contact support.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      SystemCodes?.data?.SystemCodes?.length > 0 &&
      !States &&
      !where?.StatusRecIdRef
    ) {
      const BillingVerifiedState = SystemCodes.data.SystemCodes.find(
        s => s.CodeId === "BV"
      );
      setStates({
        RouteVerified: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "RV"
        ),
        BillingVerified: BillingVerifiedState
      });
      setWhere({ StatusRecIdRef: BillingVerifiedState.RecId });
    }
  }, [States, SystemCodes, SystemCodes.data, where]);

  const handleResetToRouteVerified = async selectedData => {
    try {
      if (!selectedData.length) {
        throw Error("Please select some orders to be reset");
      }
      setLoading(true);

      await runPromisesInSeries(
        selectedData.map((RecId, index) => {
          const obj = {
            mutation: UPDATE_ORDER_MUTATION,
            variables: {
              data: {
                StatusRecIdRef: States.RouteVerified.RecId
              },
              where: {
                RecId
              }
            }
          };
          if (index === selectedData.length - 1) {
            obj.refetchQueries = ["ALL_ORDERS_VIEW_BY_PAGE_QUERY"];
          }
          return () => client.mutate(obj);
        })
      );

      setLoading(false);
      toast.success("Orders reseted successfully");
    } catch (err) {
      toast.error("Error reseting Orders.");
    }
  };
  useEffect(() => {
    if (scheduledDeliveryDate) {
      setWhere(prev => ({
        ...prev,
        ScheduledDeliveryDate_lt: moment(scheduledDeliveryDate)
          .endOf("day")
          .toDate()
      }));
    }
  }, [scheduledDeliveryDate]);

  useEffect(() => {
    if (where.StatusRecIdRef && where.ScheduledDeliveryDate_lt) {
      getOrdersData({
        variables: {
          where
        }
      });
    }
  }, [getOrdersData, where]);

  if (loading || SystemCodes?.loading || !where?.StatusRecIdRef) {
    return <Loading />;
  }
  const totalBillingVerifiedOrders =
    OrdersData?.VW_OrdersByPage?.Payload?.length || 0;

  return (
    <React.Fragment>
      <OrdersTable
        callToAction1={handleResetToRouteVerified}
        columns={BILLING_VERIFIED_COLUMNS}
        initialSortBy={[
          {
            id: "BillToNumber",
            desc: false
          },
          { id: "RouteId", desc: false },
          { id: "OrderNumber", desc: false }
        ]}
        pageHeadStart={
          <React.Fragment>
            <Field kind="group">
              <Control>
                <Label>&nbsp;</Label>
                <Input
                  readOnly
                  value={`Billing Verified (${totalBillingVerifiedOrders})`}
                />
              </Control>
              <ApolloDateInput
                label="Delivery Date Through"
                name="scheduledDeliveryDate"
                size="normal"
                value={scheduledDeliveryDate}
                onChange={(name, value) => setScheduledDeliveryDate(value)}
              />
              <ApolloDateInput
                label="Invoice Date"
                name="invoiceDate"
                size="normal"
                value={invoiceDate}
                onChange={(name, value) => setInvoiceDate(value)}
              />
              {/* <Control>
                <Label>Delivery Date Through</Label>
                <Input
                  defaultValue={scheduledDeliveryDate}
                  key={scheduledDeliveryDate}
                  type="date"
                  onBlur={({ target: { value } }) => {
                    const openPeriodDate = new Date(
                      OpenFinanceCompanyPostingPeriod?.PeriodEndDateTime
                    );
                    const newDate = new Date(value);
                    if (newDate <= openPeriodDate) {
                      setScheduledDeliveryDate(value);
                    } else {
                      setScheduledDeliveryDate(
                        moment(openPeriodDate).format("YYYY-MM-DD")
                      );
                      toast.error(
                        "Delivery Date is greater than current open Posting period."
                      );
                    }
                  }}
                />
              </Control>
              <Control>
                <Label>Invoice Date</Label>
                <Input
                  defaultValue={invoiceDate}
                  key={invoiceDate}
                  type="date"
                  onBlur={({ target: { value } }) => {
                    const openPeriodDate = new Date(
                      OpenFinanceCompanyPostingPeriod?.PeriodEndDateTime
                    );
                    const newDate = new Date(value);
                    if (newDate <= openPeriodDate) {
                      setInvoiceDate(value);
                    } else {
                      setInvoiceDate(
                        moment(openPeriodDate).format("YYYY-MM-DD")
                      );
                      toast.error(
                        "Invoice is greater than current open Posting period."
                      );
                    }
                  }}
                />
              </Control> */}
              <Control>
                <Label>Current Posting Period</Label>
                <Input
                  readOnly
                  type="date"
                  value={moment(
                    OpenFinanceCompanyPostingPeriod?.PeriodEndDateTime
                  ).format("YYYY-MM-DD")}
                />
              </Control>
              <Control>
                <Label>&nbsp;</Label>
                <Button
                  color="primary"
                  disabled={!scheduledDeliveryDate}
                  onClick={handleGenerateInvoices}
                >
                  <Icon>
                    <FontAwesomeIcon icon="file-invoice" />
                  </Icon>
                  <span>Generate Invoices</span>
                </Button>
              </Control>
            </Field>
          </React.Fragment>
        }
        selectedFlatRows={selectedFlatRows}
        setSelectedFlatRows={setSelectedFlatRows}
        where={where}
        onCellClick={cell => {
          if (cell.column.id !== "selection") {
            history.push(`/dispatch/orders/${cell.row.original.RecId}/BOL-ELD`);
          }
        }}
      />
    </React.Fragment>
  );
};
export default BillingVerified;
